const OPEN_GENERATED_SIDEBAR_EVENT = 'martini:open-generated-sidebar'
const OPEN_REQUEST_REPLAY_MS = 5000

let lastOpenGeneratedSidebarRequestAt = 0

export function emitOpenGeneratedSidebarRequest(): void {
  if (typeof window === 'undefined') return
  lastOpenGeneratedSidebarRequestAt = Date.now()
  window.dispatchEvent(new Event(OPEN_GENERATED_SIDEBAR_EVENT))
}

export function onOpenGeneratedSidebarRequest(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(OPEN_GENERATED_SIDEBAR_EVENT, listener)

  let replayTimer: number | null = null
  const shouldReplay =
    lastOpenGeneratedSidebarRequestAt > 0 && Date.now() - lastOpenGeneratedSidebarRequestAt <= OPEN_REQUEST_REPLAY_MS
  if (shouldReplay) {
    replayTimer = window.setTimeout(listener, 0)
  }

  return () => {
    window.removeEventListener(OPEN_GENERATED_SIDEBAR_EVENT, listener)
    if (replayTimer !== null) {
      window.clearTimeout(replayTimer)
    }
  }
}

// --- "Saved to library" notice ---------------------------------------------
// Fired when an asset is captured into the library from the canvas, so the
// always-mounted left panel can surface a "Saved to library · Open library"
// toast (the capture itself is silent otherwise — a save into a panel the
// user has never opened is a dead end).
const LIBRARY_SAVED_EVENT = 'martini:library-saved'
const LIBRARY_SAVE_FAILED_EVENT = 'martini:library-save-failed'
export type LibrarySaveFailureReason = 'generic' | 'access-denied'

export function emitLibrarySavedNotice(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(LIBRARY_SAVED_EVENT))
}

export function onLibrarySavedNotice(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(LIBRARY_SAVED_EVENT, listener)
  return () => window.removeEventListener(LIBRARY_SAVED_EVENT, listener)
}

export function emitLibrarySaveFailedNotice(reason: LibrarySaveFailureReason = 'generic'): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<LibrarySaveFailureReason>(LIBRARY_SAVE_FAILED_EVENT, { detail: reason }))
}

export function onLibrarySaveFailedNotice(listener: (reason: LibrarySaveFailureReason) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => listener((event as CustomEvent<LibrarySaveFailureReason>).detail ?? 'generic')
  window.addEventListener(LIBRARY_SAVE_FAILED_EVENT, handler)
  return () => window.removeEventListener(LIBRARY_SAVE_FAILED_EVENT, handler)
}

// --- "Reveal in library" request --------------------------------------------
// Fired when a flow wants the left panel OPEN on the Library section focused on
// a collection (e.g. the onboarding reveal announcing the extracted cast, or a
// library chip click in the prompt box). An optional focusAssetId additionally
// opens that asset's detail inside the focused collection.
// Replays like the sidebar-open request above: the emitter may fire before the
// project canvas (and its always-mounted left panel) has subscribed.
const LIBRARY_REVEAL_EVENT = 'martini:library-reveal'

interface LibraryRevealDetail {
  collectionIds: string[]
  focusAssetId?: string
  refetch: boolean
}

let lastLibraryRevealRequest: { detail: LibraryRevealDetail; at: number } | null = null

export function emitLibraryRevealRequest(
  collectionIds: string[],
  focusAssetId?: string,
  options: { refetch?: boolean } = {},
): void {
  if (typeof window === 'undefined') return
  const detail: LibraryRevealDetail = { collectionIds, focusAssetId, refetch: options.refetch ?? true }
  lastLibraryRevealRequest = { detail, at: Date.now() }
  window.dispatchEvent(new CustomEvent(LIBRARY_REVEAL_EVENT, { detail }))
}

export function onLibraryRevealRequest(
  listener: (collectionIds: string[], focusAssetId: string | undefined, refetch: boolean) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<LibraryRevealDetail>).detail
    listener(detail?.collectionIds ?? [], detail?.focusAssetId, detail?.refetch ?? true)
  }
  window.addEventListener(LIBRARY_REVEAL_EVENT, handler)

  let replayTimer: number | null = null
  const last = lastLibraryRevealRequest
  if (last && Date.now() - last.at <= OPEN_REQUEST_REPLAY_MS) {
    replayTimer = window.setTimeout(
      () => listener(last.detail.collectionIds, last.detail.focusAssetId, last.detail.refetch),
      0,
    )
  }

  return () => {
    window.removeEventListener(LIBRARY_REVEAL_EVENT, handler)
    if (replayTimer !== null) {
      window.clearTimeout(replayTimer)
    }
  }
}
